import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth, db } from '../firebase'; // Adjust path if needed
import { collection, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa'; // Importing FaSpinner
import './spinner-style.css'
import higherMindLogo from '../assets/images/logo.png';

const INVALID_LINK_MESSAGE = 'Set password link is invalid or has expired.';

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams();
  const [verified, setVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [apiError, setApiError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [message, setMessage] = useState('');
  const [focusedInput, setFocusedInput] = useState(null); // Track focused input
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false); // New state for loading

  const oobCode = params.get('oobCode');

  useEffect(() => {
    if (!oobCode) {
      setApiError('Invalid or missing password reset code.');
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setEmail(email);
        setVerified(true);
      })
      .catch(() => setApiError(INVALID_LINK_MESSAGE));
  }, [oobCode]);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }
    try {
      setLoading(true); // Set loading to true when the reset starts
      await confirmPasswordReset(auth, oobCode, newPassword);

      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDocRef = querySnapshot.docs[0].ref; // first matched document
        await updateDoc(userDocRef, {
          isNewUser: false,
        });
      } else {
        console.error('User not found for email:', email);
      }

      setMessage('Password has been set successfully!');
    } catch (err) {
      setApiError(err.message);
    } finally {
      if (!apiError) {
        setTimeout(() => {
          setLoading(false);
          navigate('/app-redirect', {
            state: {
              password: newPassword,
              email: email
            }
          })
        }, 1500);
      }
    }
  };

  const handleFocus = (inputName) => {
    setFocusedInput(inputName);
  };

  const handleBlur = () => {
    setFocusedInput(null);
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const inputStyle = (inputName) => ({
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '1rem',
    border: `2px solid ${focusedInput === inputName ? '#f3c300' : '#ddd'}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    width: '100%',
    boxSizing: 'border-box',
  });

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img alt="Higher Mind logo" src={higherMindLogo} width={100} height={100} style={{ borderRadius: 20, alignSelf: 'center' }} />
        <h2 style={styles.title}>Set Your Password</h2>

        {apiError && <p style={styles.error}>{apiError}</p>}
        {message && <p style={styles.success}>{message}</p>}
        {apiError === INVALID_LINK_MESSAGE && <p>Need a new link? Just open the app, enter your email, and hit 'Resend Email'. We'll send a fresh link to your inbox.</p>}

        {!apiError && !message && verified && (
          <>
            <p style={styles.subText}>
              Setting password for <strong>{email}</strong>
            </p>
            <form onSubmit={handleReset}>
              <div style={styles.inputContainer}>
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => handleFocus('newPassword')}
                  onBlur={handleBlur}
                  required
                  style={inputStyle('newPassword')}
                />
                <div style={styles.eyeButton} onClick={togglePasswordVisibility}>
                  {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                </div>
              </div>

              <div style={styles.inputContainer}>
                <input
                  type={confirmPasswordVisible ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onFocus={() => handleFocus('confirmPassword')}
                  onBlur={handleBlur}
                  required
                  style={inputStyle('confirmPassword')}
                />
                <div style={styles.eyeButton} onClick={toggleConfirmPasswordVisibility}>
                  {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                </div>
              </div>
              {!!passwordError && <p style={{ ...styles.error, marginTop: '0' }}>{passwordError}</p>}
              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? (
                  <FaSpinner className="spinner" style={{ fontSize: '1.5rem' }} />
                ) : (
                  'Set Password'
                )}
              </button>
            </form>
          </>
        )}

        {!apiError && !message && !verified && (
          <p style={{ textAlign: 'center' }}>Verifying set password link...</p>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    height: '100vh',
    margin: 0,
    backgroundColor: '#f7f7f7',
  },
  card: {
    marginTop: '7rem',
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
    width: '400px',
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    position: 'absolute',

  },
  title: {
    marginBottom: '1rem',
    textAlign: 'center',
    color: '#222',
  },
  subText: {
    fontSize: '0.95rem',
    marginBottom: '1rem',
    color: '#555',
    textAlign: 'center',
  },
  inputContainer: {
    position: 'relative',
  },
  input: {
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '1rem',
    border: '2px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    width: '100%',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '3px',
    top: '25px',
    padding: '10px',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#f3c300',
    color: '#000',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.3s ease',
    fontWeight: 'bold',
  },
  error: {
    color: '#c0392b',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  success: {
    color: '#27ae60',
    textAlign: 'center',
  },
};

document.body.style.overflow = 'hidden';

export default ResetPasswordPage;
